import { useState } from 'react';
import { toast } from 'sonner';

import { useImportTradeLinks, type TradeLinkImportFailure } from './queries';
import { IMPORT_FORMATS, type ImportFormat } from './schemas';

import { humanErrorMessage } from '@/api/errors';
import { useClients } from '@/clients/queries';
import { FileUploadDialog } from '@/components/file-upload-dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const MAX_BYTES = 25 * 1024 * 1024;

export interface TradeLinkImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultClientId?: string;
  onDone?: (failed: TradeLinkImportFailure[]) => void;
}

export const TradeLinkImportDialog = ({
  open,
  onOpenChange,
  defaultClientId,
  onDone,
}: TradeLinkImportDialogProps) => {
  const { data: clients } = useClients();
  const [clientId, setClientId] = useState(defaultClientId ?? '');
  const [format, setFormat] = useState<ImportFormat>('csv');
  const importTradeLinks = useImportTradeLinks();

  const handleSubmit = (file: File): void => {
    if (!clientId) {
      toast.error('Выберите клиента');
      return;
    }
    importTradeLinks.mutate(
      { clientId, format, file },
      {
        onSuccess: (result) => {
          toast.success(
            `Импортировано: ${String(result.imported.length)} · ошибки: ${String(result.failed.length)}`,
          );
          onDone?.(result.failed);
          onOpenChange(false);
        },
        onError: (err) => toast.error(humanErrorMessage(err)),
      },
    );
  };

  return (
    <FileUploadDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Импорт trade-links"
      description="CSV/JSON/text файл с trade-link URL'ами. Ошибочные строки будут показаны отдельно."
      maxBytes={MAX_BYTES}
      onSubmit={handleSubmit}
      isPending={importTradeLinks.isPending}
      submitLabel="Импортировать"
      extras={
        <>
          <div className="space-y-2">
            <Label>Клиент</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger>
                <SelectValue placeholder="Выберите клиента…" />
              </SelectTrigger>
              <SelectContent>
                {(clients ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Формат</Label>
            <Select
              value={format}
              onValueChange={(v) => {
                setFormat(v as ImportFormat);
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {IMPORT_FORMATS.map((f) => (
                  <SelectItem key={f} value={f}>
                    {f}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </>
      }
    />
  );
};
